/**
 * Admin Onboarding Readiness Service
 *
 * MEMBER-ONBOARDING-DATA-READINESS-REPORT-1 Phase 2: typed client for the
 * Platform-Admin-only Onboarding Readiness endpoint:
 *
 *   GET /verascore/admin/onboarding-readiness/:identifier
 *   GET /verascore/admin/onboarding-readiness/lookup?identifier=<email|auth0>
 *
 * Auth: session cookie (credentials: 'include') + Platform Admin gate on server.
 * Purpose: all calls tagged 'compliance_audit' per PRIV-20.3 convention.
 * Mirrors adminScoringAuditService fetch/auth/header conventions.
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;
const PURPOSE_HEADER = 'x-request-purpose';
const PURPOSE_VALUE = 'compliance_audit';

// ============================================================================
// Types — mirror the server contract (adminOnboardingReadinessHandler v0.1.2)
// ============================================================================

export type SectionStatus = 'GREEN' | 'YELLOW' | 'RED';
export type IdentifierType = 'uuid' | 'auth0' | 'email';

export interface SectionBase {
  status: SectionStatus;
  [key: string]: unknown;
}

// BUG-CRS-LATEPAY-VERIFY-01: per-tradeline content detail. The Tradelines
// section carries these so the "Show tradeline detail" expander can surface
// the derogatory signal (charge-offs, collections, 30/60/90 lates) that the
// category roll-up alone hides.
export interface TradelineDetail {
  creditor_name: string | null;
  account_type: string | null;
  tradeline_category: string | null;
  status_reported: string | null;
  current_rating_type: string | null;
  times_30_days_late: number | null;
  times_60_days_late: number | null;
  times_90_days_late: number | null;
  chargeoff_amount: number | null;
  most_recent_adverse_date: string | null;
  balance_reported: number | null;
  credit_limit: number | null;
  opened_date: string | null;
  bureau_report_date: string | null;
  is_active: boolean | null;
  is_authorized_user: boolean | null;
  is_derogatory: boolean;
}

export interface TradelineDerogSummary {
  derogatory_count: number;
  chargeoff_count: number;
  collection_count: number;
  late_over_120_count: number;
  tradelines_with_any_late: number;
  total_late_events: number;
  sum_chargeoff_amount: number;
}

export interface TradelinesSection extends SectionBase {
  total: number;
  revolving: number;
  installment: number;
  mortgage: number;
  other: number;
  unclassified: number;
  authorized_user: number;
  is_matchable_true: number;
  is_verifiable_true: number;
  derog_summary?: TradelineDerogSummary;
  details?: TradelineDetail[];
}

export interface OnboardingReadinessFlags {
  unclassified_is_red: boolean;
  matcher_silence_flag: boolean;
  stale_crs_flag: boolean;
  stale_algo_run_flag: boolean;
  missing_credit_completeness_flag: boolean;
  missing_member_completeness_flag: boolean;
}

export interface OnboardingReadinessSections {
  identity?: SectionBase;
  cohort?: SectionBase;
  plaid?: SectionBase;
  accounts?: SectionBase;
  transactions?: SectionBase;
  crs?: SectionBase;
  tradelines?: TradelinesSection;
  matcher?: SectionBase;
  credit_completeness?: SectionBase;
  member_completeness?: SectionBase;
  scoring?: SectionBase;
  score_history?: SectionBase;
  exposure?: SectionBase;
  [key: string]: SectionBase | undefined;
}

export interface OnboardingReadinessResponse {
  member_id: string;
  resolved_from: { type: IdentifierType; value_redacted: string };
  report_version: string;
  overall_status: SectionStatus;
  sections: OnboardingReadinessSections;
  flags: OnboardingReadinessFlags;
  notes: string[];
  generated_at: string;
}

// ============================================================================
// Helpers
// ============================================================================

function handleAuthRedirect(status: number) {
  if (status === 401) {
    console.warn('[adminOnboardingReadinessService] 401 — redirecting to login');
    window.history.pushState({}, '', '?view=login');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

const FRIENDLY_ERRORS: Record<number, string> = {
  400: 'Invalid request — please check the member ID or email and try again.',
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to access onboarding readiness data.',
  404: 'No member found for that identifier. Please verify the UUID, email, or Auth0 ID.',
  429: 'Too many requests — please wait a moment and try again.',
  500: 'An internal server error occurred. Please try again later or contact support.',
  502: 'The server is temporarily unavailable. Please try again in a few moments.',
  503: 'The service is temporarily unavailable. Please try again in a few moments.',
};

async function parseFailure(response: Response): Promise<Error> {
  try {
    const body = await response.json();
    const serverMsg = body?.message || body?.error;
    if (serverMsg) return new Error(serverMsg);
  } catch {
    // no parseable body — fall through to friendly message
  }
  return new Error(FRIENDLY_ERRORS[response.status] || `Unexpected error (HTTP ${response.status}). Please try again.`);
}

// ============================================================================
// API
// ============================================================================

// UUID v4 regex (case-insensitive)
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function getOnboardingReadiness(
  identifier: string,
): Promise<OnboardingReadinessResponse> {
  const trimmed = identifier.trim();
  if (import.meta.env.DEV) console.log(`📡 [adminOnboardingReadinessService] getOnboardingReadiness identifier=${trimmed}`);

  // UUIDs go in the path; emails/Auth0 IDs use the lookup query-param route
  // to avoid Netlify router character restrictions.
  const isUuid = UUID_RE.test(trimmed);
  const url = isUuid
    ? `${API_BASE}/verascore/admin/onboarding-readiness/${encodeURIComponent(trimmed)}`
    : `${API_BASE}/verascore/admin/onboarding-readiness/lookup?identifier=${encodeURIComponent(trimmed)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
      [PURPOSE_HEADER]: PURPOSE_VALUE,
    },
    credentials: 'include',
  });
  if (response.status === 401 || response.status === 403) {
    if (response.status === 401) handleAuthRedirect(response.status);
    throw await parseFailure(response);
  }
  if (!response.ok) throw await parseFailure(response);
  const json = await response.json();
  return json.data ?? json;
}
